import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { invoicesService, api } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card, CardHeader, CardTitle, CardBody } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Input, Select } from '../../components/UI/Input';
import { Modal } from '../../components/UI/Modal';
import { Spinner } from '../../components/UI/Spinner';
import { ConfirmDialog } from '../../components/UI/ConfirmDialog';
import {
  ArrowLeft,
  Download,
  CreditCard,
  Edit2,
  Trash2,
  Mail,
  MapPin,
  ExternalLink,
  Clock,
  CheckCircle,
  XCircle,
  ChevronDown,
  AlertCircle,
  Plus,
} from 'lucide-react';

export const InvoiceDetail = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [histories, setHistories] = useState([]);
  const [payments, setPayments] = useState([]);
  const [amountPending, setAmountPending] = useState('0.00');
  const [loading, setLoading] = useState(true);

  // Pay modal
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('Cash');
  const [payDescription, setPayDescription] = useState('');
  const [paySubmitting, setPaySubmitting] = useState(false);

  // Status Change
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [pendingWarningOpen, setPendingWarningOpen] = useState(false);

  // Delete invoice
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Delete payment
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [deletePaymentOpen, setDeletePaymentOpen] = useState(false);

  const { success, error: toastError } = useToast();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Close status dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchInvoice = useCallback(async () => {
    setLoading(true);
    try {
      const res = await invoicesService.get(id);
      if (res && res.data && res.data.invoice) {
        setInvoice(res.data.invoice);
        setHistories(res.data.histories || []);
        setPayments(res.data.payments || []);
        setAmountPending(res.data.amount_pending || '0.00');
        setPayAmount(Number(res.data.amount_pending) > 0 ? res.data.amount_pending : '');
      }
    } catch (err) {
      toastError(err.message || 'Error al cargar factura.');
    } finally {
      setLoading(false);
    }
  }, [id, toastError]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    setPaySubmitting(true);
    try {
      await invoicesService.pay(id, {
        amount: payAmount,
        method: payMethod,
        description: payDescription,
      });
      success('Pago registrado correctamente.');
      setPayModalOpen(false);
      setPayDescription('');
      fetchInvoice();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al registrar pago.');
    } finally {
      setPaySubmitting(false);
    }
  };

  const handleDeletePayment = async () => {
    if (!paymentToDelete) return;
    try {
      await invoicesService.deletePayment(id, paymentToDelete.id);
      success('Pago eliminado e importe recalculado.');
      setDeletePaymentOpen(false);
      setPaymentToDelete(null);
      fetchInvoice();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar pago.');
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusDropdownOpen(false);
    if (Number(invoice.status) === Number(newStatus)) return;

    if (Number(newStatus) === 1 && Number(amountPending) > 0.01) {
      setPendingWarningOpen(true);
      return;
    }

    setStatusSubmitting(true);
    try {
      await invoicesService.changeStatus(id, newStatus);
      const statusLabels = { 0: 'Abierta', 1: 'Pagada', 2: 'Cerrada' };
      success(`Factura marcada como ${statusLabels[newStatus]}.`);
      fetchInvoice();
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al cambiar estado.');
    } finally {
      setStatusSubmitting(false);
    }
  };

  const handleDeleteInvoice = async () => {
    setDeleting(true);
    try {
      await invoicesService.delete(id);
      success('Factura eliminada correctamente.');
      navigate('/invoices');
    } catch (err) {
      toastError(err.data?.message || err.message || 'Error al eliminar factura.');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      await invoicesService.downloadPdf(invoice.id, invoice.number);
      success('Descargando PDF...');
    } catch (err) {
      toastError('Error al descargar archivo PDF.');
    }
  };

  const handleOpenPdf = () => {
    window.open(api.getPdfUrl('invoices', invoice.id), '_blank');
  };

  if (loading) {
    return <Spinner size="lg" label="Cargando factura..." className="py-20" />;
  }

  if (!invoice) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500">Factura no encontrada.</p>
        <Link to="/invoices" className="mt-4 inline-block">
          <Button variant="secondary" size="sm">Volver al listado</Button>
        </Link>
      </div>
    );
  }

  const items = invoice.invoiceItems || [];
  const symbol = invoice.currency?.symbol || '$';
  const totalPaid = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const totalInvoice = Number(invoice.total || 0);
  const pctPaid = totalInvoice > 0 ? Math.min(100, Math.round((totalPaid / totalInvoice) * 100)) : 0;

  const getStatusBadgeConfig = (status) => {
    switch (Number(status)) {
      case 1:
        return {
          label: 'Pagada',
          icon: CheckCircle,
          className: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800',
        };
      case 2:
        return {
          label: 'Cerrada',
          icon: XCircle,
          className: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
        };
      default:
        return {
          label: 'Abierta',
          icon: Clock,
          className: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800',
        };
    }
  };

  const statusConfig = getStatusBadgeConfig(invoice.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Top Header & Actions Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/invoices">
            <Button variant="ghost" size="sm" icon={ArrowLeft}>
              Volver
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight font-mono">
                Factura #{invoice.number || String(invoice.id).padStart(6, '0')}
              </h1>

              {/* Status Interactive Selector Dropdown */}
              <div className="relative inline-block text-left" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                  disabled={statusSubmitting}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-lg border shadow-sm transition-all hover:opacity-90 ${statusConfig.className} ${statusSubmitting ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                  title="Click para cambiar el estado de la factura"
                >
                  <StatusIcon className="w-3.5 h-3.5" />
                  <span>{statusConfig.label}</span>
                  <ChevronDown className="w-3 h-3 opacity-60 ml-0.5" />
                </button>

                {/* Dropdown Menu */}
                {statusDropdownOpen && (
                  <div className="absolute left-0 mt-1.5 w-40 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-800 py-1.5 z-30 animate-in fade-in zoom-in-95 duration-100">
                    <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 dark:border-slate-800">
                      Cambiar Estado
                    </div>
                    <button
                      type="button"
                      onClick={() => handleStatusChange(0)}
                      className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-amber-50 dark:hover:bg-amber-950/40 transition-colors ${Number(invoice.status) === 0 ? 'text-amber-600 font-bold bg-amber-50/50 dark:bg-amber-950/20' : 'text-slate-700 dark:text-slate-300'}`}
                    >
                      <Clock className="w-3.5 h-3.5 text-amber-500" />
                      Abierta
                    </button>
                    <button
                      type="button"
                      onClick={() => handleStatusChange(1)}
                      className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-colors ${Number(invoice.status) === 1 ? 'text-emerald-600 font-bold bg-emerald-50/50 dark:bg-emerald-950/20' : 'text-slate-700 dark:text-slate-300'}`}
                    >
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                      Pagada
                    </button>
                    <button
                      type="button"
                      onClick={() => handleStatusChange(2)}
                      className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${Number(invoice.status) === 2 ? 'text-slate-900 dark:text-white font-bold bg-slate-100 dark:bg-slate-800' : 'text-slate-700 dark:text-slate-300'}`}
                    >
                      <XCircle className="w-3.5 h-3.5 text-slate-500" />
                      Cerrada
                    </button>
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Emitida el {invoice.invo_date || '—'} {invoice.due_date ? `· Vence el ${invoice.due_date}` : ''}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center flex-wrap gap-2">
          <Button
            variant="primary"
            size="sm"
            icon={CreditCard}
            onClick={() => setPayModalOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700 font-bold shadow-sm text-white"
          >
            {Number(amountPending) <= 0 ? 'Registrar Abono' : 'Registrar Pago'}
          </Button>

          <Button
            variant="secondary"
            size="sm"
            icon={ExternalLink}
            onClick={handleOpenPdf}
            title="Ver PDF en navegador"
          >
            Ver PDF
          </Button>

          <Button
            variant="secondary"
            size="sm"
            icon={Download}
            onClick={handleDownloadPdf}
            title="Descargar PDF"
          >
            Descargar
          </Button>

          <Link to={`/invoices/edit/${invoice.id}`}>
            <Button variant="ghost" size="sm" icon={Edit2}>
              Editar
            </Button>
          </Link>

          <Button
            variant="ghost"
            size="sm"
            icon={Trash2}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
            onClick={() => setDeleteConfirmOpen(true)}
          >
            Eliminar
          </Button>
        </div>
      </div>

      {/* Main Invoice Card */}
      <Card className="p-8 space-y-8">
        {/* Header Metadata Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-6 border-b border-slate-100 dark:border-slate-800">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Datos del Cliente
            </span>
            <div className="mt-2 space-y-1">
              <p className="text-base font-bold text-slate-900 dark:text-slate-100">
                {invoice.customer?.full_name || 'Cliente sin nombre'}
              </p>
              {invoice.customer?.email && (
                <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  {invoice.customer.email}
                </p>
              )}
              {invoice.customer?.address1 && (
                <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  {invoice.customer.address1} {invoice.customer.address2 ? `, ${invoice.customer.address2}` : ''}
                </p>
              )}
            </div>
          </div>

          <div className="sm:text-right space-y-1">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Detalles de Cobro
            </span>
            <p className="text-xs text-slate-600 dark:text-slate-300">
              <strong>Fecha:</strong> {invoice.invo_date}
            </p>
            {invoice.due_date && (
              <p className="text-xs text-slate-600 dark:text-slate-300">
                <strong>Vencimiento:</strong> {invoice.due_date}
              </p>
            )}
            {invoice.project && (
              <p className="text-xs text-slate-600 dark:text-slate-300">
                <strong>Proyecto:</strong> {invoice.project.name}
              </p>
            )}
            <p className="text-xs text-slate-600 dark:text-slate-300">
              <strong>Moneda:</strong> {invoice.currency?.code} ({invoice.currency?.name})
            </p>
          </div>
        </div>

        {/* Itemized Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th className="py-3 px-4">Descripción</th>
                <th className="py-3 px-4 text-center">Cantidad</th>
                <th className="py-3 px-4 text-right">Precio Unitario</th>
                <th className="py-3 px-4 text-right">Importe Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map((item, idx) => (
                <tr key={idx}>
                  <td className="py-3.5 px-4 font-medium text-slate-800 dark:text-slate-200">
                    {item.description}
                  </td>
                  <td className="py-3.5 px-4 text-center text-slate-600 dark:text-slate-300 font-mono">
                    {item.quantity}
                  </td>
                  <td className="py-3.5 px-4 text-right text-slate-600 dark:text-slate-300 font-mono">
                    {symbol} {Number(item.price).toFixed(2)}
                  </td>
                  <td className="py-3.5 px-4 text-right font-bold text-slate-900 dark:text-slate-100 font-mono">
                    {symbol} {(Number(item.price) * Number(item.quantity)).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals Summary */}
        <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-end">
          <div className="w-full sm:w-72 space-y-2 text-xs">
            <div className="flex justify-between text-slate-600 dark:text-slate-400">
              <span>Total Facturado:</span>
              <span className="font-mono font-bold">
                {symbol} {totalInvoice.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-emerald-600 dark:text-emerald-400">
              <span>Total Pagado / Cobrado:</span>
              <span className="font-mono font-bold">
                - {symbol} {Number(totalPaid).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between font-bold text-base text-slate-900 dark:text-slate-100 pt-2 border-t border-slate-200 dark:border-slate-700">
              <span>Saldo Pendiente:</span>
              <span className={`font-mono font-black text-lg ${Number(amountPending) > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600'}`}>
                {symbol} {Number(amountPending).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </div>

        {/* Notes */}
        {invoice.notes && (
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 space-y-1">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
              Notas e Instrucciones de Pago
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-line leading-relaxed">
              {invoice.notes}
            </p>
          </div>
        )}
      </Card>

      {/* Payments History & Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Payments Registered */}
        <Card>
          <CardHeader
            actions={
              <Button
                size="sm"
                variant="secondary"
                icon={CreditCard}
                onClick={() => setPayModalOpen(true)}
                className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800"
              >
                Añadir Pago
              </Button>
            }
          >
            <CardTitle subtitle="Abonos y recibos vinculados a esta factura">
              Historial de Pagos ({payments.length})
            </CardTitle>
          </CardHeader>

          <CardBody className="p-0">
            {/* Payment Summary Bar */}
            <div className="p-4 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 space-y-3">
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400">Facturado</span>
                  <p className="font-mono font-black text-slate-900 dark:text-white">
                    {symbol} {totalInvoice.toFixed(2)}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400">Cobrado</span>
                  <p className="font-mono font-black text-emerald-600 dark:text-emerald-400">
                    {symbol} {Number(totalPaid).toFixed(2)}
                  </p>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-bold text-slate-400">Pendiente</span>
                  <p className={`font-mono font-black ${Number(amountPending) > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600'}`}>
                    {symbol} {Number(amountPending).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              <div>
                <div className="flex justify-between text-[11px] font-semibold text-slate-500 mb-1">
                  <span>Progreso de cobro</span>
                  <span>{pctPaid}%</span>
                </div>
                <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${pctPaid >= 100 ? 'bg-emerald-500' : 'bg-[#08707A]'}`}
                    style={{ width: `${pctPaid}%` }}
                  />
                </div>
              </div>
            </div>

            {payments.length === 0 ? (
              <div className="p-8 text-center space-y-3">
                <p className="text-xs text-slate-400">No hay pagos registrados aún para esta factura.</p>
                <Button
                  size="sm"
                  variant="secondary"
                  icon={Plus}
                  onClick={() => setPayModalOpen(true)}
                  className="mx-auto"
                >
                  Registrar Primer Pago
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-80 overflow-y-auto">
                {payments.map((p) => (
                  <div key={p.id} className="p-4 flex items-center justify-between gap-4 text-xs hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold font-mono text-slate-900 dark:text-slate-100 text-sm">
                          {symbol} {Number(p.amount).toFixed(2)}
                        </span>
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-teal-50 text-[#08707A] dark:bg-teal-950/60 dark:text-teal-300 border border-teal-200 dark:border-teal-800">
                          {p.method || 'Efectivo'}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        {new Date(p.created_at).toLocaleString()} {p.description ? `· ${p.description}` : ''}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        setPaymentToDelete(p);
                        setDeletePaymentOpen(true);
                      }}
                      className="text-slate-400 hover:text-red-600 transition-colors p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/40"
                      title="Eliminar este pago"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Audit / Status History */}
        <Card>
          <CardHeader>
            <CardTitle subtitle="Bitácora de eventos y cambios">
              Historial de Actividad
            </CardTitle>
          </CardHeader>

          <CardBody className="p-0">
            {histories.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400">
                Sin registros de historial.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-96 overflow-y-auto">
                {histories.map((h) => (
                  <div key={h.id} className="p-4 text-xs space-y-0.5">
                    <p className="font-bold text-slate-800 dark:text-slate-200">
                      {h.title || 'Evento de factura'}
                    </p>
                    <p className="text-slate-500 dark:text-slate-400">
                      {h.description}
                    </p>
                    <p className="text-[10px] text-slate-400 pt-0.5">
                      {new Date(h.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Record Payment Modal */}
      <Modal
        isOpen={payModalOpen}
        onClose={() => !paySubmitting && setPayModalOpen(false)}
        title="Registrar Abono o Pago"
        subtitle={`Saldo pendiente: ${symbol} ${Number(amountPending).toFixed(2)}`}
      >
        <form onSubmit={handleRecordPayment} className="p-6 space-y-4">
          <Input
            label="Monto a Cobrar"
            type="number"
            step="0.01"
            min="0.01"
            placeholder="0.00"
            value={payAmount}
            onChange={(e) => setPayAmount(e.target.value)}
            required
            autoFocus
          />

          <Select
            label="Método de Pago"
            value={payMethod}
            onChange={(e) => setPayMethod(e.target.value)}
          >
            <option value="Cash">Efectivo</option>
            <option value="Bank Transfer">Transferencia Bancaria</option>
            <option value="Credit Card">Tarjeta de Crédito / Débito</option>
            <option value="PayPal">PayPal</option>
            <option value="Check">Cheque</option>
            <option value="Other">Otro</option>
          </Select>

          <Input
            label="Nota / Referencia de Pago (Opcional)"
            placeholder="Ej: Transferencia Banco XYZ Ref #12345"
            value={payDescription}
            onChange={(e) => setPayDescription(e.target.value)}
          />

          <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setPayModalOpen(false)}
              disabled={paySubmitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={paySubmitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Guardar Pago
            </Button>
          </div>
        </form>
      </Modal>

      {/* Pending Balance Warning / Quick Pay Prompt */}
      <Modal
        isOpen={pendingWarningOpen}
        onClose={() => setPendingWarningOpen(false)}
        title="Saldo Pendiente Requerido"
        subtitle={`Factura #${invoice?.number || ''}`}
      >
        <div className="p-6 space-y-4">
          <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-xs space-y-1">
              <p className="font-bold text-amber-900 dark:text-amber-200">
                La factura no puede marcarse como PAGADA directamente.
              </p>
              <p className="text-amber-700 dark:text-amber-300">
                Existe un saldo pendiente de{' '}
                <strong>
                  {symbol} {Number(amountPending).toFixed(2)}
                </strong>
                . El sistema requiere que los pagos registrados cubran el total de la factura antes de pasar a estado Pagada.
              </p>
            </div>
          </div>

          <p className="text-xs text-slate-600 dark:text-slate-300">
            ¿Deseas registrar un pago por el monto restante de{' '}
            <strong className="text-slate-900 dark:text-white">
              {symbol} {Number(amountPending).toFixed(2)}
            </strong>{' '}
            para marcarla automáticamente como Pagada?
          </p>

          <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
            <Button variant="secondary" onClick={() => setPendingWarningOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              className="bg-emerald-600 hover:bg-emerald-700"
              icon={CreditCard}
              onClick={() => {
                setPendingWarningOpen(false);
                setPayAmount(amountPending);
                setPayModalOpen(true);
              }}
            >
              Registrar Saldo Restante
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Payment Confirm */}
      <ConfirmDialog
        isOpen={deletePaymentOpen}
        onClose={() => setDeletePaymentOpen(false)}
        onConfirm={handleDeletePayment}
        title="¿Eliminar registro de pago?"
        message={`Se recalculará el saldo pendiente de la factura automáticamente.`}
        confirmText="Eliminar Pago"
      />

      {/* Delete Invoice Confirm */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteInvoice}
        title="¿Eliminar factura?"
        message="¿Estás seguro de que deseas eliminar esta factura y sus pagos asociados?"
        confirmText="Eliminar Factura"
        isLoading={deleting}
      />
    </div>
  );
};
